<template>
    <div class="home-page">
        <NoticeBar :visible="noticeVisible" :message="noticeMessage" :type="noticeType" :actions="noticeActions"
            @close="noticeVisible = false" />
        <Mytitle />

        <!-- 主内容区域 -->
        <div class="main-content dashboard-grid">
            <!-- 左侧导航 -->
            <Sidebar :current-nav="currentNav" :current-sub-nav="currentSubNav" :expanded-navs="expandedNavs"
                @toggle-nav="toggleNav" @switch-nav="switchNav" @logout="handleLogout" />

            <!-- 右侧内容 -->
            <div class="content dashboard-content">
                <!-- 根据导航显示不同组件 -->
                <!-- 考试文件 -->
                <SectionList v-if="currentNav === '考试文件'" :section-key="sectionKey" :title="currentSubNav || '考试文件'"
                    :reset-token="sectionResetToken" />

                <!-- 选择考试 -->
                <div v-else-if="currentNav === '选择考试'" class="module-panel">
                    <div class="panel-head">
                        <div>
                            <h2>选择考试</h2>
                            <p>通过搜索、分类和时间筛选快速定位目标考试。</p>
                        </div>
                        <div class="panel-actions">
                            <button class="btn-page" @click="resetFilters">重置条件</button>
                        </div>
                    </div>
                    <div class="exam-toolbar">
                        <input class="exam-search" v-model="examKeyword" type="text" placeholder="关键字搜索考试名称"
                            @keyup.enter="searchExams" />
                        <select class="exam-select" v-model.number="selectedCategory">
                            <option value="">全部分类</option>
                            <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                        </select>
                        <input class="exam-date" v-model="enrollStartFrom" type="date" placeholder="报名开始 ≥" />
                        <input class="exam-date" v-model="enrollEndTo" type="date" placeholder="报名结束 ≤" />
                        <select class="exam-select" v-model="sortField">
                            <option value="">默认排序</option>
                            <option value="enroll_start_time">按报名开始</option>
                            <option value="enroll_end_time">按报名结束</option>
                            <option value="title">按名称</option>
                        </select>
                        <select class="exam-select" v-model="sortOrder">
                            <option value="asc">升序</option>
                            <option value="desc">降序</option>
                        </select>
                        <button class="btn-search" @click="searchExams">搜索</button>
                    </div>
                    <ExamTable :exams="examList" :show-action="currentSubNav === '报名中'" @register="openRegistration"
                        @view="openExamDetailPage" />
                    <!-- 内联考试详情展示 -->
                    <div v-if="examDetail" class="exam-detail-panel">
                        <h3>考试详情</h3>
                        <div class="detail-grid">
                            <div><strong>考试名称：</strong>{{ examDetail.title || examDetail.name }}</div>
                            <div><strong>报名时间：</strong>{{ formatDate(examDetail.enroll_start_time) }} - {{
                                formatDate(examDetail.enroll_end_time) }}</div>
                            <div><strong>考试时间：</strong>{{ examDetail.ticket_time_text || (examDetail.start_time || examDetail.end_time ? (formatDate(examDetail.start_time) + ' - ' + formatDate(examDetail.end_time)) : '待定') }}</div>
                            <div><strong>报名费：</strong>{{ formatFee(examDetail.fee) }}</div>
                            <div><strong>状态：</strong>{{ mapExamStatus(examDetail) }}</div>
                            <div v-if="examDetail.description"><strong>说明：</strong>{{ examDetail.description }}</div>
                        </div>
                        <div class="detail-actions">
                            <button class="btn-page" @click="examDetail = null">收起</button>
                            <button v-if="currentSubNav === '报名中'" class="btn-search"
                                @click="openRegistration(examDetail)">去报名</button>
                        </div>
                    </div>
                    <div class="pagination">
                        <span class="pagination-total">共 {{ total }} 条</span>
                        <select v-model.number="pageSize" @change="handlePageSizeChange" :disabled="examsLoading" class="page-size-select">
                            <option :value="8">8条/页</option>
                            <option :value="10">10条/页</option>
                            <option :value="20">20条/页</option>
                            <option :value="50">50条/页</option>
                        </select>
                        <button class="btn-page" @click="prevPage" :disabled="page <= 1 || examsLoading">‹</button>
                        <span class="page-current">{{ page }}</span>
                        <button class="btn-page" @click="nextPage" :disabled="!canNext || examsLoading">›</button>
                    </div>
                </div>

                <!-- 我的报名 -->
                <ScoreQuery v-else-if="currentNav === '我的报名' && currentSubNav === '成绩查询'" />
                <AdmissionTicketPage v-else-if="currentNav === '我的报名' && currentSubNav === '准考证打印'"
                    @back="handleBackFromTicket" />
                <ExamInfo v-else-if="currentNav === '我的报名'" :current-sub-nav="currentSubNav"
                    :personal-info="personalInfo" :exam-info="examInfo" @photo-upload="handlePhotoUpload"
                    @open-ticket-list="openTicketList" />

                <!-- 个人中心 -->
                <PersonalCenter v-else-if="currentNav === '个人中心'" :current-sub-nav="currentSubNav"
                    @logout="handleLogout" />
            </div>
        </div>
        <PublicSiteFooter />
    </div>
</template>

<script>
import Mytitle from "../assets/title.vue"
import PublicSiteFooter from "./PublicSiteFooter.vue"
import Sidebar from "./Sidebar.vue"
import NoticeBar from "./NoticeBar.vue"
import FileList from "./FileList.vue"
import SectionList from "./SectionList.vue"
import ExamTable from "./ExamTable.vue"
import AdmissionTicketPage from "./AdmissionTicketPage.vue"
import ScoreQuery from "./ScoreQuery.vue"
import PersonalCenter from "./PersonalCenter.vue"
import ExamInfo from "./ExamInfo.vue"
import { listActiveExams, listEndedExams, listCategories, getExamDetail } from "../api/exams"
import { logout as authLogout } from "../api/auth"
import { clearTokens, clearUser } from "../api/storage"

export default {
    name: 'Home',
    components: {
        Mytitle,
        PublicSiteFooter,
        Sidebar,
        NoticeBar,
        FileList,
        SectionList,
        ExamTable,
        PersonalCenter,
        ExamInfo,
        AdmissionTicketPage,
        ScoreQuery
    },
    data() {
        return {
            currentNav: '考试文件',
            currentSubNav: '报考文件',
            sectionResetToken: 0,
            // 存储每个导航项的展开状态
            expandedNavs: {
                '考试文件': true,  // 默认展开考试文件
                '选择考试': false,
                '我的报名': false,
                '个人中心': false
            },
            // 考试数据
            // 取消本地模拟数据，统一使用后端返回的数据
            // 后端拉取的考试列表与状态
            examsFetched: [],
            examsLoading: false,
            examsError: '',
            // 选择考试：查询与分页
            examKeyword: '',
            page: 1,
            pageSize: 8,
            total: 0,
            // 选择考试：筛选与排序
            categories: [],
            selectedCategory: '',
            enrollStartFrom: '',
            enrollEndTo: '',
            sortField: '',
            sortOrder: 'asc',
            // 报名信息数据
            personalInfo: [
                { label: '姓名', value: '王烁' },
                { label: '证件类型', value: '身份证' },
                { label: '证件号码', value: '152631********0010' },
                { label: '民族', value: '汉族' },
                { label: '政治面貌', value: '中共党员' },
                { label: '出生日期', value: '1995-01-01' },
                { label: '联系电话', value: '13456789111' },
                { label: '邮箱', value: '13456789111@163.com' },
                { label: '户籍所在地', value: '内蒙古自治区呼和浩特市赛罕区' },
                { label: '毕业院校', value: '内蒙古大学' },
                { label: '专业', value: '计算机科学与技术' },
                { label: '学历', value: '本科' },
                { label: '学位', value: '学士' }
            ],
            examInfo: [
                { label: '考试名称', value: '内蒙古自治区2023年事业单位考试' },
                { label: '报考岗位', value: '信息管理岗' },
                { label: '报名状态', value: '已报名' },
                { label: '审核状态', value: '审核通过' },
                { label: '缴费状态', value: '已缴费' },
                { label: '考试时间', value: '2023-04-15 09:00-11:30' },
                { label: '考试地点', value: '呼和浩特市第一中学' },
                { label: '准考证号', value: '202303150001' },
                { label: '座位号', value: '01' },
                { label: '考试科目', value: '职业能力倾向测验、综合应用能力' },
                { label: '成绩状态', value: '待公布' }
            ],
            // 报名表单相关状态（改为新页面展示，不再使用）
            examDetail: null,
            noticeVisible: false,
            noticeMessage: '',
            noticeType: 'info',
            noticeActions: []
        }
    },
    methods: {
        // 执行退出登录（支持从侧边栏传入已确认标记）
        handleLogout(confirmed = false) {
            if (!confirmed) {
                // 兜底：若未在侧边栏确认，则此处二次确认
                this.noticeMessage = '确定要退出登录吗？'
                this.noticeType = 'warning'
                this.noticeActions = [
                    { label: '取消', onClick: () => { this.noticeVisible = false } },
                    { label: '确定', primary: true, onClick: () => { this.noticeVisible = false; this.handleLogout(true) } }
                ]
                this.noticeVisible = true
                return;
            }
            authLogout().catch(() => {/* 忽略后端错误，前端仍清理 */ });
            clearTokens();
            clearUser();
            window.location.hash = 'login';
            setTimeout(() => window.location.reload(), 100);
        },
        openExamDetailPage(exam) {
            try {
                const arr = JSON.parse(localStorage.getItem('app_history') || '[]')
                arr.push({ hash: '#home', nav: '选择考试', subNav: this.currentSubNav })
                localStorage.setItem('app_history', JSON.stringify(arr.slice(-20)))
            } catch (_) { }
            window.location.hash = `exam-detail/${exam?.id}`
        },
        // 切换导航
        switchNav(nav, subNav) {
            this.currentNav = nav
            this.currentSubNav = subNav || ''
            // 在考试文件菜单下，每次点击子项均触发内容重置（返回列表）
            if (nav === '考试文件') {
                this.sectionResetToken += 1
            }
            if (this.currentNav === '选择考试') {
                // 确保子导航是有效的值，否则默认使用"报名中"
                if (this.currentSubNav !== '报名中' && this.currentSubNav !== '报名结束') {
                    this.currentSubNav = '报名中'
                }
                this.page = 1
                this.fetchExams()
            }
        },
        // 计算考试状态
        mapExamStatus(exam = {}) {
            // 根据报名时间段严格计算状态，不依赖当前子导航
            const now = new Date()
            const s = exam.enroll_start_time ? new Date(exam.enroll_start_time) : null
            const e = exam.enroll_end_time ? new Date(exam.enroll_end_time) : null
            const examEnd = exam.end_time ? new Date(exam.end_time) : null
            const validS = s && !isNaN(s.getTime())
            const validE = e && !isNaN(e.getTime())
            const validExamEnd = examEnd && !isNaN(examEnd.getTime())

            if (validS && now < s) return '未开始'
            if (validE && now <= e) return '报名中'
            if (validE && now > e) return '报名结束'
            // 新增：只有开始时间且当前时间 >= 开始时间时，显示为报名中
            if (validS && !validE && now >= s) return '报名中'
            if (validExamEnd && now > examEnd) return '考试结束'

            // 兜底：使用后端状态文本或发布状态
            if (exam.status_text) return exam.status_text
            if (exam.status === 1) return '已发布'
            return '—'
        },
        // 显示考试详情（当前页内联）
        async showExamDetail(exam) {
            try {
                // 若考试已绑定二级目录，则直接跳转到内容详情
                const boundId = exam?.section_item?.id || exam?.section_item_id
                if (boundId) {
                    // 记录来源页为首页
                    try { sessionStorage.setItem('last_page', window.location.hash || '#home') } catch (e) { }
                    window.location.hash = `section/${boundId}`
                    return
                }
                const id = exam?.id
                this.examDetail = null
                const detail = await getExamDetail(id)
                // 如果详情中存在绑定，则跳转；否则在页面展示详情
                const detailBoundId = detail?.section_item?.id || detail?.section_item_id
                if (detailBoundId) {
                    // 记录来源页为首页
                    try { sessionStorage.setItem('last_page', window.location.hash || '#home') } catch (e) { }
                    window.location.hash = `section/${detailBoundId}`
                    return
                }
                this.examDetail = { ...exam, ...detail }
            } catch (e) {
                console.error('获取考试详情失败', e)
                this.examDetail = { ...exam }
            }
        },
        // 通用日期格式化
        formatDate(d) {
            if (!d) return '-'
            const dt = new Date(d)
            if (isNaN(dt.getTime())) return d
            const y = dt.getFullYear()
            const m = String(dt.getMonth() + 1).padStart(2, '0')
            const da = String(dt.getDate()).padStart(2, '0')
            const hh = String(dt.getHours()).padStart(2, '0')
            const mm = String(dt.getMinutes()).padStart(2, '0')
            return `${y}-${m}-${da} ${hh}:${mm}`
        },
        // 通用费用格式化
        formatFee(value) {
            const num = Number(value ?? 0)
            if (Number.isNaN(num)) return '¥0.00'
            return '¥' + num.toFixed(2)
        },
        // 切换导航展开/收起状态
        toggleNav(nav) {
            // 先关闭所有其他展开的导航栏
            Object.keys(this.expandedNavs).forEach(key => {
                if (key !== nav) {
                    this.expandedNavs[key] = false;
                }
            });
            // 再切换当前点击导航栏的状态
            this.expandedNavs[nav] = !this.expandedNavs[nav]
        },
        // 跳转到报名新页面（拦截已报名用户并跳转“我的报名”）
        async openRegistration(exam) {
            const id = exam?.id || ''
            // 未登录直接进入报名页（由报名页再提示登录）
            const token = localStorage.getItem('user_access_token') || ''
            if (!token) {
                window.location.hash = `exam-register/${id}`
                return
            }
            try {
                const res = await fetch('/api/exam/applications/my/', {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                if (res && res.ok) {
                    let list
                    try {
                      list = await res.json()
                    } catch (e) {
                      console.error('解析报名列表失败', e)
                      list = []
                    }
                    const found = Array.isArray(list) && list.some(x => String(x?.exam?.id || x?.exam_id || '') === String(id))
                    if (found) {
                        this.showNotice('您已报名该考试，请在“我的报名”中修改信息', 'info')
                        // 前端导航切换到“我的报名”
                        this.currentNav = '我的报名'
                        this.currentSubNav = '报名信息查询'
                        this.expandedNavs['我的报名'] = true
                        localStorage.setItem('redirect_to_my_registration', '1')
                        return
                    }
                }
            } catch (e) {
                // 静默失败，默认跳转报名页
                console.warn('检查已报名失败，将继续跳转报名页', e)
            }
            try {
                const arr = JSON.parse(localStorage.getItem('app_history') || '[]')
                arr.push({ hash: '#home', nav: this.currentNav, subNav: this.currentSubNav })
                localStorage.setItem('app_history', JSON.stringify(arr.slice(-20)))
            } catch (_) { }
            window.location.hash = `exam-register/${id}`
        },
        _readHistory() { try { return JSON.parse(localStorage.getItem('app_history') || '[]') } catch (_) { return [] } },
        _pushHistory(state) { const arr = this._readHistory(); arr.push(state); localStorage.setItem('app_history', JSON.stringify(arr.slice(-20))) },
        async fetchExams() {
            this.examsError = ''
            this.examsLoading = true
            try {
                // 确保只在"选择考试"导航下才获取考试数据
                if (this.currentNav !== '选择考试') {
                    this.examsFetched = []
                    this.examsLoading = false
                    return
                }

                const params = { keyword: this.examKeyword || '', page: this.page, page_size: this.pageSize }
                if (this.selectedCategory) params.category_id = this.selectedCategory
                if (this.enrollStartFrom) params['enroll_start_time__gte'] = this.enrollStartFrom
                if (this.enrollEndTo) params['enroll_end_time__lte'] = this.enrollEndTo
                if (this.sortField) params.ordering = this.sortOrder === 'desc' ? ('-' + this.sortField) : this.sortField

                // 根据子导航选择正确的API
                // "报名中" -> listActiveExams, "报名结束" -> listEndedExams
                // 如果currentSubNav不是这两个值，默认使用"报名中"
                const shouldUseActive = this.currentSubNav === '报名中'
                const apiCall = shouldUseActive ? listActiveExams : listEndedExams

                console.log('获取考试数据，当前子导航:', this.currentSubNav, '使用API:', shouldUseActive ? 'listActiveExams' : 'listEndedExams', '参数:', params)
                const resp = await apiCall(params)
                console.log('后端返回的数据:', resp)
                let list = []
                let count = 0

                // 处理分页数据结构 { count: 100, results: [...] } 或 [...]
                if (resp && Array.isArray(resp.results)) {
                    list = resp.results
                    count = resp.count || 0
                } else if (Array.isArray(resp)) {
                    // 如果后端没有返回分页结构（兼容旧逻辑），则前端需要手动分页
                    count = resp.length
                    // 这里不需要再手动切片，因为如果后端没分页，说明数据量小或者逻辑不对
                    // 但通常 Django DRF 分页开启后都会返回 { count, results }
                    // 如果确实返回了数组，说明后端没开启分页，则全部显示
                    list = resp
                } else {
                    list = []
                    count = 0
                }

                this.total = count

                const mapItem = (x) => {
                    let status = '未开始'
                    let statusClass = 'status-pending'
                    let action = '查看'

                    const now = new Date()
                    const s = x.enroll_start_time ? new Date(x.enroll_start_time) : null
                    const e = x.enroll_end_time ? new Date(x.enroll_end_time) : null
                    const examEnd = x.end_time ? new Date(x.end_time) : null

                    // 没有报名开始时间，状态显示"已发布"，按钮禁用
                    if (!s) {
                        status = '已发布'
                        statusClass = 'status-active'
                        action = '报名'  // 保留报名按钮，但会被禁用
                        return { ...x, status, statusClass, action, disabled: true, enrollTimeText: '未公布' }
                    } else if (now < s) {
                        status = '未开始'
                        statusClass = 'status-pending'
                        action = '查看'
                    } else if (e && now > e) {
                        status = '报名结束'
                        statusClass = 'status-ended'
                        action = '查看'
                    } else if (examEnd && now > examEnd) {
                        status = '考试结束'
                        statusClass = 'status-ended'
                        action = '查看'
                    } else if ((!e && now >= s) || (e && now >= s && now <= e)) {
                        // 有开始时间且当前时间 >= 开始时间时，显示为报名中
                        status = '报名中'
                        statusClass = 'status-active'
                        action = '报名'
                    } else if (x.status_text) {
                        status = x.status_text
                    } else if (x.status === 1) {
                        status = '已发布'
                        statusClass = 'status-active'
                        action = '查看'  // 没有明确报名时间，只能查看
                    }
                    return { ...x, status, statusClass, action }
                }

                // 确保list是数组后再进行map操作
                const safeList = Array.isArray(list) ? list : []
                let mapped = safeList.map(mapItem)

                // 数据验证：确保是考试数据而不是其他数据
                mapped = mapped.filter(item => {
                    // 确保有title字段
                    if (!item.title) {
                        console.warn('过滤掉无title的考试数据:', item)
                        return false
                    }
                    // 确保有id字段
                    if (!item.id) {
                        console.warn('过滤掉无id的考试数据:', item)
                        return false
                    }
                    return true
                })

                console.log('处理后的考试数据:', mapped, '总数:', mapped.length)
                // 注意：如果是在"报名中"子导航，不应该再在前端进行状态过滤，
                // 因为这会导致总数(total)和当前页显示数量不一致（后端返回了10条，前端滤掉了3条，显示7条，但分页器显示有下一页）
                // 应该依赖后端接口 listActiveExams/listEndedExams 的过滤逻辑
                // if (this.currentSubNav === '报名中') {
                //   mapped = mapped.filter(x => x.status !== '报名结束')
                // }
                this.examsFetched = mapped
            } catch (e) {
                console.error(e)
                const status = e?.response?.status
                if (status === 401) {
                    this.examsError = '登录已过期，请重新登录'
                    setTimeout(() => {
                        window.location.hash = 'login'
                    }, 2000)
                } else if (status === 403) {
                    this.examsError = '无权限查看考试列表'
                } else if (status >= 500) {
                    this.examsError = '服务器错误，请稍后重试'
                } else {
                    this.examsError = '考试列表加载失败，请检查网络连接'
                }
                this.examsFetched = []
            } finally {
                this.examsLoading = false
            }
        },
        searchExams() {
            this.page = 1
            this.fetchExams()
        },
        resetFilters() {
            this.examKeyword = ''
            this.selectedCategory = ''
            this.enrollStartFrom = ''
            this.enrollEndTo = ''
            this.sortField = ''
            this.sortOrder = 'asc'
            this.page = 1
            this.fetchExams()
        },
        async loadCategories() {
            try {
                const res = await listCategories()
                this.categories = Array.isArray(res) ? res : (res?.results || [])
            } catch (e) {
                console.error('分类加载失败', e)
                this.categories = []
            }
        },
        prevPage() {
            if (this.page > 1) {
                this.page -= 1
                this.fetchExams()
            }
        },
        nextPage() {
            if (this.canNext) {
                this.page += 1
                this.fetchExams()
            }
        },
        handlePageSizeChange() {
            this.page = 1
            // 确保 pageSize 是数字类型
            this.pageSize = Number(this.pageSize)
            this.fetchExams()
        },
        // 处理照片上传
        handlePhotoUpload(event) {
            // 这里可以添加照片上传的逻辑
            const file = event.target.files[0]
            if (file) {
                console.log('上传的照片:', file)
                // 模拟上传成功
                this.showNotice('照片上传成功！', 'success')
            }
        },
        handleBackFromTicket() {
            this.currentNav = '我的报名'
            this.currentSubNav = '我的报名'
            this.expandedNavs['我的报名'] = true
        },
        showNotice(message, type = 'info', actions = [], autoHideMs = 2500) {
            this.noticeMessage = message
            this.noticeType = type
            this.noticeActions = actions
            this.noticeVisible = true
            if (autoHideMs > 0) setTimeout(() => { this.noticeVisible = false }, autoHideMs)
        }
        ,
        openTicketList() {
            this.currentNav = '我的报名'
            this.currentSubNav = '准考证打印'
            this.expandedNavs['我的报名'] = true
        }
    }
    ,
    mounted() {
        if (this.currentNav === '选择考试') {
            this.fetchExams()
        }
        this.loadCategories()
        // ESC 快捷键：收起所有二级菜单
        try {
            this._escHandler = (e) => {
                if (e && (e.key === 'Escape' || e.keyCode === 27)) {
                    Object.keys(this.expandedNavs).forEach(k => { this.expandedNavs[k] = false })
                }
            }
            window.addEventListener('keydown', this._escHandler)
        } catch (_) { /* noop */ }
        // 报名成功后跳回首页，自动切换到“我的报名”
        try {
            const needRedirect = localStorage.getItem('redirect_to_my_registration')
            if (needRedirect === '1') {
                this.currentNav = '我的报名'
                this.currentSubNav = '我的报名'
                this.expandedNavs['我的报名'] = true
                localStorage.removeItem('redirect_to_my_registration')
            }
            const toTicketHome = localStorage.getItem('redirect_to_ticket_home')
            if (toTicketHome === '1') {
                this.currentNav = '我的报名'
                this.currentSubNav = '准考证打印'
                this.expandedNavs['我的报名'] = true
                localStorage.removeItem('redirect_to_ticket_home')
            }
            const sectionBack = localStorage.getItem('redirect_to_section_home')
            if (sectionBack) {
                this.currentNav = '考试文件'
                this.currentSubNav = sectionBack
                this.expandedNavs['考试文件'] = true
                localStorage.removeItem('redirect_to_section_home')
            }
            // 从详情页返回时，自动切换到“选择考试-报名中”
            const toActiveExams = localStorage.getItem('redirect_to_active_exams')
            if (toActiveExams === '1') {
                this.currentNav = '选择考试'
                this.currentSubNav = '报名中'
                this.expandedNavs['选择考试'] = true
                localStorage.removeItem('redirect_to_active_exams')
                this.page = 1
                this.fetchExams()
            }
            const nav = localStorage.getItem('redirect_home_nav')
            const subNav = localStorage.getItem('redirect_home_subnav')
            if (nav) {
                this.currentNav = nav
                this.currentSubNav = subNav || ''
                if (nav in this.expandedNavs) this.expandedNavs[nav] = true
                localStorage.removeItem('redirect_home_nav')
                localStorage.removeItem('redirect_home_subnav')
                if (nav === '选择考试') {
                    this.page = 1
                    this.fetchExams()
                }
            }
        } catch (e) { /* noop */ }
    },
    beforeUnmount() {
        try { if (this._escHandler) window.removeEventListener('keydown', this._escHandler) } catch (_) { /* noop */ }
    },
    watch: {
        currentSubNav(newVal, oldVal) {
            if (this.currentNav === '选择考试' && newVal !== oldVal) {
                this.page = 1
                this.fetchExams()
            }
        },
        pageSize(newVal) {
            // 当每页条数变化时，重置到第一页并重新加载
            this.page = 1
            this.fetchExams()
        }
    },
    computed: {
        sectionKey() {
            if (this.currentNav !== '考试文件') return ''
            const map = {
                '报考文件': 'baokao',
                '网上报名流程': 'liucheng',
                '照片上传说明': 'photo',
                '登录说明': 'login',
                '网上支付说明': 'payment'
            }
            return map[this.currentSubNav] || 'baokao'
        },
        examList() {
            if (this.currentNav !== '选择考试') return []
            // this.examsFetched 已经是完整的一页数据了（由后端分页返回）
            // 不需要再 slice，否则会导致只显示第一页的头8条
            return Array.isArray(this.examsFetched) ? this.examsFetched : []
        },
        canNext() {
            // 若后端返回总数，则用总数判断；否则不再分页
            if (this.total && this.total > 0) {
                return this.page * this.pageSize < this.total
            }
            return false
        }
    }
}
</script>

<style scoped>
.home-page {
    min-height: 100vh;
    background:
        radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 24%),
        radial-gradient(circle at top right, rgba(15, 23, 42, 0.08), transparent 20%),
        linear-gradient(180deg, #f6f9ff 0%, #eef3fb 100%);
}

.main-content {
    display: flex;
    margin: 20px 0;
    gap: 20px;
    padding: 0 20px 24px;
}

.dashboard-grid {
    align-items: flex-start;
}

.content {
    flex: 1;
    padding: 20px;
    background: rgba(255, 255, 255, 0.84);
    border-radius: 24px;
    box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.7);
    min-height: calc(100vh - var(--header-height, 100px) - 40px);
    height: auto;
    overflow: visible;
    backdrop-filter: blur(14px);
}

.module-panel {
    display: grid;
    gap: 16px;
}

.panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.panel-head h2 {
    margin: 0;
    color: #0f172a;
    font-size: 24px;
}

.panel-head p {
    margin: 6px 0 0;
    color: #64748b;
}

.panel-actions {
    display: flex;
    gap: 10px;
}

.exam-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 16px;
    border-radius: 18px;
    background: #f8fbff;
    border: 1px solid rgba(148, 163, 184, 0.16);
}

.exam-search {
    flex: 1 1 260px;
    padding: 10px 12px;
    border: 1px solid #dbe3f0;
    border-radius: 12px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.exam-search:focus,
.exam-select:focus,
.exam-date:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
}

.exam-select,
.exam-date {
    padding: 10px 12px;
    border: 1px solid #dbe3f0;
    border-radius: 12px;
    background: #fff;
    outline: none;
}

.btn-search,
.btn-page {
    border: 0;
    border-radius: 12px;
    padding: 10px 14px;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.btn-search {
    background: linear-gradient(135deg, #2563eb, #4f46e5);
    color: #fff;
    box-shadow: 0 10px 20px rgba(37, 99, 235, 0.18);
}

.btn-page {
    background: #eef4ff;
    color: #1d4ed8;
}

.btn-search:hover,
.btn-page:hover {
    transform: translateY(-1px);
}

.pagination {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    justify-content: flex-end;
    flex-wrap: wrap;
}

.pagination .pagination-total {
    color: #64748b;
    font-size: 14px;
}

.page-select,
.page-size-select {
    padding: 10px 12px;
    border: 1px solid #dbe3f0;
    border-radius: 12px;
    cursor: pointer;
    background-color: white;
}

.btn-page:disabled,
.btn-search:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination .page-current {
    min-width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #2563eb;
    color: #fff;
    border-radius: 10px;
    font-size: 13px;
}

.exam-detail-panel {
    margin-top: 16px;
    padding: 18px;
    border: 1px solid rgba(148, 163, 184, 0.16);
    border-radius: 18px;
    background: linear-gradient(180deg, #ffffff, #f8fbff);
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 8px;
}

.detail-actions {
    margin-top: 14px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.loading-text,
.page-info {
    color: #64748b;
}

@media (max-width: 768px) {
    .main-content {
        flex-direction: column;
        gap: 16px;
        padding: 0 14px 20px;
    }

    .content {
        width: 100%;
        padding: 16px;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }

    .panel-head {
        flex-direction: column;
    }
}

@media (max-width: 600px) {
    .exam-toolbar {
        padding: 12px;
    }

    .pagination {
        justify-content: flex-start;
    }
}
</style>
